import { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import theme from '../../../theme/theme';
import { useFetch } from '../../../hooks/useFetch';
import { GenFilesApiFactory } from '../api/gen-files.api';
import { useCurrentGenFile } from '../hooks/useCurrentGenFile';
import { GeneralChannelCard } from '../components/GeneralChannelCard/GeneralChannelCard';
import { UploadGenModal } from '../components/UploadGenModal/UploadGenModal';
import { GEN_COLORS, GEN_CARD, GEN_PAGE } from '../constants/gen-colors';
import { FEATURE_FLAGS } from '../../../shared/featureFlags';

const { font } = GEN_CARD;
const page = GEN_PAGE;

export function GeneralSettingsPage() {
  const { t } = useTranslation('generalSettings');
  const [client] = useFetch();
  const api = useMemo(() => GenFilesApiFactory(client), [client]);
  const { channelState, loading, error, refresh } = useCurrentGenFile();
  const [uploadOpen, setUploadOpen] = useState(false);
  const showPilotChannel = FEATURE_FLAGS.PILOT_GEN_CHANNEL;

  const hasGeneral = Boolean(channelState?.current);
  const totalInstances = 0;
  const upToDate = 0;
  const pending = 0;

  return (
    <Box>
      {/* Page header */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          background: alpha(theme.palette.primary.main, 0.06),
          color: theme.palette.text.body,
        }}
      >
        <Box
          sx={{
            width: theme.avatar.md,
            height: theme.avatar.md,
            borderRadius: theme.borderRadius.md,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <SettingsOutlinedIcon
            sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: theme.typography.employeeName.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.body,
            }}
          >
            {t('page.title')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: page.contentPadding }}>
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('page.loadError')}
          </Alert>
        )}
        {loading && !channelState && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && (
          <>
            {/* Diffusion summary banner */}
            {hasGeneral ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: page.bannerGap,
                    flexWrap: 'wrap',
                    bgcolor: theme.palette.common.white,
                    border: `1px solid ${GEN_COLORS.border}`,
                    borderRadius: theme.borderRadius.sm,
                    p: page.bannerPadding,
                    mb: page.bannerMarginBottom,
                  }}
                >
                  <Typography
                    sx={{
                      flex: '0 0 100%',
                      fontSize: page.bannerHeaderFontSize,
                      fontWeight: theme.typography.fontWeightSemiBold,
                      letterSpacing: GEN_CARD.font.letterSpacingLabel,
                      textTransform: 'uppercase',
                      color: theme.palette.text.subtle,
                    }}
                  >
                    {t('banner.title')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: page.bannerNumberSize,
                        fontWeight: theme.typography.fontWeightSemiBold,
                        color: GEN_COLORS.ok,
                      }}
                    >
                      {upToDate}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: page.bannerLabelSize,
                        color: theme.palette.text.muted,
                      }}
                    >
                      {t('banner.upToDate')}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: page.bannerSepSize,
                      color: GEN_COLORS.borderStrong,
                    }}
                  >
                    /
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: page.bannerNumberSize,
                        fontWeight: theme.typography.fontWeightSemiBold,
                        color: GEN_COLORS.accent,
                      }}
                    >
                      {pending}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: page.bannerLabelSize,
                        color: theme.palette.text.muted,
                      }}
                    >
                      {t('banner.pending')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      ml: 'auto',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: page.bannerTotalFontSize,
                        fontWeight: theme.typography.fontWeightMedium,
                        color: theme.palette.text.subtle,
                      }}
                    >
                      {t('banner.outOf', { total: totalInstances })}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 100%', mt: page.bannerBarMt }}>
                    <Box
                      sx={{
                        height: GEN_CARD.size.barHeight,
                        borderRadius: theme.borderRadius.pill,
                        bgcolor: GEN_COLORS.offBg,
                        overflow: 'hidden',
                        display: 'flex',
                      }}
                    >
                      {totalInstances > 0 && upToDate > 0 && (
                        <Box
                          sx={{
                            width: `${(upToDate / totalInstances) * 100}%`,
                            height: '100%',
                            bgcolor: GEN_COLORS.ok,
                          }}
                        />
                      )}
                      {totalInstances > 0 && pending > 0 && (
                        <Box
                          sx={{
                            width: `${(pending / totalInstances) * 100}%`,
                            height: '100%',
                            bgcolor: GEN_COLORS.accent,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: page.bannerNoticeGap,
                    p: page.bannerNoticePadding,
                    borderRadius: theme.borderRadius.xs,
                    border: `1px solid ${GEN_COLORS.border}`,
                    bgcolor: theme.palette.background.section,
                    mb: page.bannerNoticeMarginBottom,
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: GEN_CARD.size.iconCallout,
                      mt: '1px',
                      color: theme.palette.text.subtle,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: font.sizeBody,
                      lineHeight: page.bannerNoticeLineHeight,
                      color: theme.palette.text.muted,
                    }}
                  >
                    <Trans
                      i18nKey="banner.autoNotice"
                      ns="generalSettings"
                      components={{ strong: <strong /> }}
                    />
                  </Typography>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: page.bannerNoticeGap,
                  p: page.bannerNoticePadding,
                  borderRadius: theme.borderRadius.xs,
                  border: `1px solid ${GEN_COLORS.border}`,
                  bgcolor: theme.palette.background.section,
                  mb: page.bannerNoticeMarginBottom,
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: GEN_CARD.size.iconCallout,
                    mt: '1px',
                    color: theme.palette.text.subtle,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: font.sizeBody,
                    lineHeight: font.lineHeightRelaxed,
                    color: theme.palette.text.muted,
                  }}
                >
                  {t('banner.emptyNotice')}
                </Typography>
              </Box>
            )}

            {/* Two-card grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: page.gridGap,
              }}
            >
              <GeneralChannelCard
                channel="general"
                channelState={channelState}
                onUpload={() => setUploadOpen(true)}
              />
              {showPilotChannel && (
                <GeneralChannelCard
                  channel="pilote"
                  channelState={null}
                  onUpload={() => setUploadOpen(true)}
                />
              )}
              {!showPilotChannel && <Box sx={{ visibility: 'hidden' }} aria-hidden="true" />}
            </Box>
          </>
        )}
      </Box>

      <UploadGenModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        api={api}
        currentVersion={channelState?.current?.version ?? null}
        onSuccess={refresh}
      />
    </Box>
  );
}
